// Ops recovery.  /analytics/ops-recovery
//
// Dependency readiness and the dead-letter recovery queue. Both counters here
// are counts of rows that exist, so a zero is a real measurement: nothing
// failed. There is no rate on this screen and therefore nothing to gate.

import { useState } from "react";

import { http, qs } from "../api";
import {
  Badge,
  Banner,
  Confirm,
  Field,
  SkeletonRows,
  Spinner,
  Table,
  useAsync,
  useToast,
  type BadgeTone,
  type Column,
} from "../ui";
import { StatTile } from "../charts";
import {
  DependencyList,
  errText,
  stamp,
  titleCase,
  type DependenciesResponse,
} from "./shared";

interface DLQItem {
  id: string;
  organization_id: string;
  source_topic: string;
  original_topic: string;
  original_key?: string | null;
  correlation_id?: string | null;
  error_code: string;
  error_message?: string | null;
  status: string;
  attempt: number;
  max_attempts: number;
  payload?: Record<string, unknown> | null;
  safe_details?: Record<string, unknown> | null;
  created_at: string;
  updated_at?: string;
  last_error_at?: string | null;
  retried_at?: string | null;
  discarded_at?: string | null;
  reviewed_by_id?: string | null;
  reviewer_note?: string | null;
}

interface DLQListResponse {
  items: DLQItem[];
}

const DLQ_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "failed", label: "Failed" },
  { value: "retry_pending", label: "Retry pending" },
  { value: "retried", label: "Retried" },
  { value: "blocked", label: "Blocked" },
  { value: "discarded", label: "Discarded" },
];

function dlqTone(status: string): BadgeTone {
  if (status === "retried") return "good";
  if (status === "blocked") return "warning";
  if (status === "discarded") return "neutral";
  return "critical";
}

/** Says up front why a retry will or will not be accepted, before it is pressed. */
function retryHelp(item: DLQItem): string {
  if (item.status === "retried") return "This item was republished to its original supported topic.";
  if (item.status === "blocked") return "Retry is blocked because the item reached its attempt limit.";
  if (
    !item.original_topic.includes(".knowledge.source.ingest.") &&
    !item.original_topic.includes(".knowledge.chunk.ready.")
  ) {
    return "Retry is supported only for Knowledge Centre ingestion and chunk-ready topics.";
  }
  return "Retry republishes this payload to its original topic and records an audit entry.";
}

export function OpsRecoveryView() {
  const toast = useToast();
  const [status, setStatus] = useState("failed");
  const [version, setVersion] = useState(0);
  const [selected, setSelected] = useState<DLQItem | null>(null);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  const [confirm, setConfirm] = useState<"retry" | "discard" | null>(null);

  const listQuery = useAsync(
    () => http.get<DLQListResponse>(`/ops/dlq${qs({ status, limit: 100 })}`),
    [status, version]
  );
  const depsQuery = useAsync(() => http.get<DependenciesResponse>("/ops/dependencies"), [version]);

  const items = listQuery.data?.items ?? [];
  const dependencies = depsQuery.data?.dependencies ?? [];

  const failedCount = items.filter((i) => i.status === "failed" || i.status === "blocked").length;
  const dependencyIssues = dependencies.filter((d) => d.required && d.status !== "ok").length;

  const select = async (item: DLQItem) => {
    // Shown from the list row first so the panel never sits blank, then replaced
    // by the full record — the list omits the payload.
    setSelected(item);
    setNote(item.reviewer_note ?? "");
    try {
      setSelected(await http.get<DLQItem>(`/ops/dlq/${item.id}`));
    } catch (err) {
      toast.push(errText(err, "Could not load that DLQ item"), "critical");
    }
  };

  const runAction = async (kind: "retry" | "discard") => {
    if (!selected) return;
    setActing(true);
    try {
      const updated = await http.post<DLQItem>(
        `/ops/dlq/${selected.id}/${kind}`,
        kind === "retry" ? { note, force: false } : { note }
      );
      setSelected(updated);
      toast.push(
        kind === "retry" ? "DLQ item republished" : "DLQ item discarded with audit record",
        "good"
      );
      setVersion((v) => v + 1);
    } catch (err) {
      toast.push(
        errText(err, kind === "retry" ? "Retry blocked or failed" : "Could not discard this item"),
        "critical"
      );
      throw err;
    } finally {
      setActing(false);
    }
  };

  const columns: Column<DLQItem>[] = [
    {
      key: "original_topic",
      header: "Topic",
      sortable: true,
      render: (r) => (
        <div>
          <div className="strong truncate">{r.original_topic}</div>
          <div className="muted truncate">{r.correlation_id || r.id}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (r) => <Badge tone={dlqTone(r.status)}>{r.status}</Badge>,
    },
    { key: "error_code", header: "Error", sortable: true },
    {
      key: "attempt",
      header: "Attempts",
      align: "right",
      sortable: true,
      render: (r) => (
        <span className="num">
          {r.attempt}/{r.max_attempts}
        </span>
      ),
    },
    {
      key: "last_error_at",
      header: "Last error",
      sortable: true,
      render: (r) => <span className="nowrap">{stamp(r.last_error_at || r.created_at)}</span>,
    },
  ];

  const terminal = selected?.status === "retried" || selected?.status === "discarded";

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Ops recovery</h1>
          <div className="sub">Dependency readiness and the dead-letter recovery queue.</div>
        </div>
        <div className="page-actions">
          <select
            value={status}
            aria-label="Queue status"
            style={{ width: 170 }}
            onChange={(e) => setStatus(e.target.value)}
          >
            {DLQ_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button className="btn" disabled={listQuery.loading} onClick={() => setVersion((v) => v + 1)}>
            {listQuery.loading ? <Spinner label="Refreshing" /> : "⟲"} Refresh
          </button>
        </div>
      </div>

      {listQuery.error && (
        <Banner tone="critical" traceId={listQuery.error.traceId}>
          Could not load the dead-letter queue: {listQuery.error.message}
        </Banner>
      )}
      {depsQuery.error && (
        <Banner tone="warning" traceId={depsQuery.error.traceId}>
          Dependency probes did not load: {depsQuery.error.message}
        </Banner>
      )}

      <div className="ops-split">
        <div className="stack">
          <div className="grid c3">
            <StatTile label="DLQ failures" value={failedCount} deltaLabel="failed or blocked" />
            <StatTile label="Dependency issues" value={dependencyIssues} deltaLabel="required and not ok" />
            <StatTile label="Queue filter" value={titleCase(status || "all")} />
          </div>

          <div className="card flush">
            <div className="card-head">
              <h3>Dead-letter queue</h3>
              <span className="muted">Newest first, up to 100</span>
            </div>
            <Table
              columns={columns}
              rows={items}
              loading={listQuery.loading}
              empty="No DLQ items match this filter"
              onRowClick={(row) => void select(row)}
              selectedId={selected?.id ?? null}
            />
          </div>
        </div>

        <div className="stack">
          <div className="card">
            <h3>Dependencies</h3>
            {depsQuery.loading ? (
              <SkeletonRows n={4} cols={2} />
            ) : (
              <div style={{ marginTop: "var(--sp-3)" }}>
                <DependencyList rows={dependencies} />
              </div>
            )}
          </div>

          <div className="card">
            <h3>DLQ detail</h3>
            {!selected ? (
              <p className="muted" style={{ marginTop: "var(--sp-3)" }}>
                Select a failed item to inspect its payload and recovery actions.
              </p>
            ) : (
              <div className="col" style={{ marginTop: "var(--sp-3)" }}>
                <div className="muted">{selected.id}</div>
                <div className="strong prewrap">{selected.error_message || selected.error_code}</div>
                <Banner tone="info">{retryHelp(selected)}</Banner>
                <Field label="Operator note">
                  <input
                    value={note}
                    placeholder="Why you are retrying or discarding"
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>
                <div className="row">
                  <button
                    className="btn primary"
                    disabled={acting || terminal}
                    onClick={() => setConfirm("retry")}
                  >
                    Retry
                  </button>
                  <button className="btn danger" disabled={acting} onClick={() => setConfirm("discard")}>
                    Discard
                  </button>
                </div>
                <pre className="pre-block">
                  {JSON.stringify(selected.payload || selected.safe_details || {}, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>

      <Confirm
        open={confirm === "retry"}
        onClose={() => setConfirm(null)}
        onConfirm={() => runAction("retry")}
        title="Republish this event?"
        confirmLabel="Retry"
        body={
          <>
            <p>
              This republishes the payload to <strong>{selected?.original_topic}</strong> and records
              an audit entry. Attempt {(selected?.attempt ?? 0) + 1} of {selected?.max_attempts ?? 0}.
            </p>
            <p className="muted">Downstream consumers will process the event again.</p>
          </>
        }
      />
      <Confirm
        open={confirm === "discard"}
        onClose={() => setConfirm(null)}
        onConfirm={() => runAction("discard")}
        title="Discard this event?"
        danger
        confirmLabel="Discard"
        body={
          <p>
            The event will never be processed. The row and your note are kept for audit, but the work
            it represents is dropped.
          </p>
        }
      />
    </div>
  );
}

